/**
 * Consent registry (decision 18) — `GET /consents/current` / `POST /consents/accept`.
 * The case transformer camelises both bodies. The banner and the sign-up checkbox show the
 * kinds of `mustAccept`; while `placeholders` is true the texts are drafts (shown anyway — the
 * founder wants the banner live; launching in the EU on drafts is an INFRA red, not ours).
 */

export type ConsentKind = 'terms' | 'privacy' | 'cookies'

export interface ConsentDocument {
  kind: ConsentKind
  version: string
  locale: string
  bodyUrl: string | null
  effectiveFrom: string | null
  isPlaceholder: boolean
  acceptedVersion: string | null
  acceptedAt: string | null
  mustAccept: boolean
}

export interface ConsentsCurrent {
  documents: ConsentDocument[]
  mustAccept: ConsentKind[]
  placeholders: boolean
}

export type ConsentSource = 'signup' | 'settings' | 'banner'

export interface ConsentsAcceptRequest {
  documents: { kind: ConsentKind; version: string }[]
  source: ConsentSource
}

const KINDS: readonly ConsentKind[] = ['terms', 'privacy', 'cookies']

function isKind(value: unknown): value is ConsentKind {
  return typeof value === 'string' && (KINDS as readonly string[]).includes(value)
}

/** Whatever the wire holds → a clean registry answer (unknown kinds dropped). */
export function normalizeConsentsCurrent(raw: unknown): ConsentsCurrent {
  const body = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const documents: ConsentDocument[] = []
  for (const item of Array.isArray(body.documents) ? body.documents : []) {
    const doc = (item ?? {}) as Record<string, unknown>
    if (!isKind(doc.kind) || typeof doc.version !== 'string') continue
    documents.push({
      kind: doc.kind,
      version: doc.version,
      locale: typeof doc.locale === 'string' ? doc.locale : 'en',
      bodyUrl: typeof doc.bodyUrl === 'string' ? doc.bodyUrl : null,
      effectiveFrom: typeof doc.effectiveFrom === 'string' ? doc.effectiveFrom : null,
      isPlaceholder: doc.isPlaceholder === true,
      acceptedVersion: typeof doc.acceptedVersion === 'string' ? doc.acceptedVersion : null,
      acceptedAt: typeof doc.acceptedAt === 'string' ? doc.acceptedAt : null,
      mustAccept: doc.mustAccept === true,
    })
  }
  const mustAccept = (Array.isArray(body.mustAccept) ? body.mustAccept : []).filter(isKind)
  return { documents, mustAccept, placeholders: body.placeholders === true || documents.some((d) => d.isPlaceholder) }
}

/** The documents still to accept, in the registry's order. */
export function consentsToAccept(current: ConsentsCurrent | null | undefined): ConsentDocument[] {
  if (!current) return []
  return current.documents.filter((doc) => doc.mustAccept || current.mustAccept.includes(doc.kind))
}

/** The accept body for exactly the versions the user was shown. */
export function consentsAcceptRequest(
  documents: readonly Pick<ConsentDocument, 'kind' | 'version'>[],
  source: ConsentSource,
): ConsentsAcceptRequest {
  return { documents: documents.map(({ kind, version }) => ({ kind, version })), source }
}

/** 409 `consent_version_not_current` — a version moved under the user: re-read and ask again. */
export function isStaleConsentVersion(error: unknown): boolean {
  const response = (error as { response?: { status?: number } } | null)?.response
  return response?.status === 409
}

/* ── Sign-up: the versions the checkbox showed, accepted after the first token ──── */

/** Sign-up has no token (it comes with the e-mail link, maybe in another tab): kept until then. */
export const PENDING_SIGNUP_CONSENTS_KEY = 'fair.consents.pendingSignup'

export function writePendingSignupConsents(documents: readonly Pick<ConsentDocument, 'kind' | 'version'>[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      PENDING_SIGNUP_CONSENTS_KEY,
      JSON.stringify(documents.map(({ kind, version }) => ({ kind, version }))),
    )
  } catch {
    // Private mode: the banner asks again after sign-in.
  }
}

/** Reads AND clears the pending sign-up acceptance (one shot). */
export function takePendingSignupConsents(): { kind: ConsentKind; version: string }[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(PENDING_SIGNUP_CONSENTS_KEY)
    window.localStorage.removeItem(PENDING_SIGNUP_CONSENTS_KEY)
    const parsed = JSON.parse(raw ?? '[]') as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item): item is { kind: ConsentKind; version: string } =>
        Boolean(item) && isKind((item as { kind?: unknown }).kind) && typeof (item as { version?: unknown }).version === 'string',
    )
  } catch {
    return []
  }
}
